import React, {useContext, useEffect, useState, useRef} from "react";
import axios from "@common/axios";
import Button from "@common/Button";
import {UserContext} from "@common/context/UserContext";
import ButtonsGroup from "@common/ButtonsGroup";
import {Carousel} from "antd";
import Card, {CARD} from "@common/Card";
import {PlayArrow, Pause} from "@mui/icons-material"

interface GameType {
    [key: string]: any;
    active: boolean;
}

const Games: React.FC = props => {
    const [games, setGames] = useState<GameType[]>([]);
    const [selectedGame, setSelectedGame] = useState<GameType | null>();
    const [isCarouselPlaying, setCarouselPlaying] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>();
    const {user} = useContext(UserContext);

    useEffect(() => {
        loadGames(true);
    }, []);

    useEffect(() => {
        if (selectedGame && selectedGame.active) {
            const contentPanel: HTMLElement = document.getElementById('contentPanel');
            contentPanel.scrollTo({ top: 0, behavior: "smooth" });
            const iframe = iframeRef.current;
            if (iframe) {
                iframe.onload = () => {
                    iframe.contentWindow.addEventListener('gameLoaded', () => {
                        console.log('Game has loaded');
                    });
                };
            }
        }
    }, [selectedGame?.active]);

    const loadGamesData = ():void => {
        axios.get('/web/ui/games/getGames')
            .then(response => {
                setGames(response.data.games);
            });
    }

    const loadGames = (refreshData: boolean = false): void => {
        if (refreshData) loadGamesData();
        setSelectedGame(null);
        setCarouselPlaying(false);
    }

    const loadGameDetails = (game: GameType): void => {
        setSelectedGame({...game, active: false});
    }

    const loadGame = (game: GameType): void => {
        setSelectedGame({...game, active: true});
        pauseCarousel();
    }

    const pauseCarousel = (): void => {
        setCarouselPlaying(false);
    }

    const resumeCarousel = (): void => {
        setCarouselPlaying(true);
    }

    if (selectedGame != null && selectedGame.active == true) {
        let height :number = selectedGame.info.height;
        if (height == null) {
            if (selectedGame.mediaSubType === "GAME_JAVASCRIPT") height = 560;
            if (selectedGame.mediaSubType === "GAME_HTML") height = 640;
        }
        let width :number = selectedGame.info.width;
        if (width == null) {
            if (selectedGame.mediaSubType === "GAME_JAVASCRIPT") width = 920;
            if (selectedGame.mediaSubType === "GAME_HTML") width = 854;
        }
        const ratio: number = height / width;
        const iFrameWidth :number = 900;
        const iFrameHeight :number = iFrameWidth * ratio;
        return (
            <div>
                <ButtonsGroup position="right">
                    <Button onClick={() => loadGames(true)}>Back</Button>
                </ButtonsGroup>
                <div className="flexCentre">
                    <iframe ref={iframeRef} className="flex" src={`/controlCentre/loadGame.action?gameId=${selectedGame.mediaId}`} height={iFrameHeight}
                            width={iFrameWidth} frameBorder="0"></iframe>
                </div>
            </div>
        )
    } else if (selectedGame != null && selectedGame.active != true) {
        return (
            <div id="gameInfo" className="flex">
                <div className="flexColumn flex2 margin-r--m">
                    <h2>{selectedGame.info.name}</h2>
                    <p>{selectedGame.info.description}</p>
                    <div>
                        <h3>Scores</h3>
                        <div className="flexRow">
                            <div className="flexColumn margin-r--l">
                                <h4>Highest score</h4>
                                <p>
                                    <strong>{selectedGame.info.highScore}</strong> points
                                </p>
                            </div>
                            {!user.publicSession && (
                                <div className="flexColumn margin-r--l">
                                    <h4>Personal best</h4>
                                    <p>
                                        <strong>{selectedGame.info.userHighScore}</strong>
                                    </p>
                                </div>
                            )}
                            {selectedGame.info.school != null && (
                                <div className="flexColumn margin-r--l">
                                    <h4>{selectedGame.info.school}'s highest score</h4>
                                    <p>
                                        <strong>{selectedGame.info.schoolHighScore}</strong> points
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <ButtonsGroup className="margin-t--l">
                        <Button type={"text"} onClick={() => loadGames()}>Back</Button>
                        <Button onClick={() => loadGame(selectedGame)}>Play</Button>
                    </ButtonsGroup>
                </div>
                <div className="flexColumn flex2 margin-l--m" style={{position: "relative"}}>
                    <div className="ch-carousel-controls">
                        {isCarouselPlaying ? (
                            <Button type={"text"} onClick={pauseCarousel} style={{transform: 'translate(-50%, -50%)'}} aria-label={'Pause carousel'}><Pause style={{width: '48px', height: '48px'}} /></Button>
                        ) : (
                            <Button type={"text"} onClick={resumeCarousel} style={{transform: 'translate(-50%, -50%)'}} aria-label={'Play carousel'}><PlayArrow style={{width: '48px', height: '48px'}} /></Button>
                        )}
                    </div>
                    <Carousel arrows autoplay={isCarouselPlaying} pauseOnHover={false} style={{position: "absolute", width: '100%'}}>
                        {selectedGame.info.thumbnailUrls.map((thumbnailUrl, index) => (
                            <img key={index} src={thumbnailUrl}/>
                        ))}
                    </Carousel>
                </div>
            </div>
        )
    } else {
        return (
            <div className="flexCentre">
                <div id="allGames" className="flexRow flexWrap flexCentre extraLargeGap" style={{maxWidth: '1100px'}}>
                    {games.map((game, index) => (
                        <Card
                            key={game.mediaId}
                            padding={CARD.PADDING.NONE}
                            style={{width: '300px', overflow: 'hidden'}}
                        >
                            <figure className="margin--rm">
                                <img src={game.info.thumbnailUrls?.[0]} role={'presentation'} alt={''} style={{width: '100%'}}/>
                            </figure>
                            <div className="padding-x--m padding-t--m">
                                <h2>{game.mediaName}</h2>
                                <p>Personal Best: <strong>{game.info.userHighScore}</strong> points</p>
                            </div>
                            <ButtonsGroup className="margin--l" position="right">
                                <Button type="outline" onClick={() => loadGameDetails(game)}>Details</Button>
                                <Button onClick={() => loadGame(game)}>Play</Button>
                            </ButtonsGroup>
                        </Card>
                    ))}
                </div>
            </div>
        )
    }
}

export default Games;